import React, { useState, useMemo } from 'react';
import { 
  ArrowLeft, 
  AlertTriangle, 
  Shield, 
  Brain, 
  Eye,
  Loader,
  CheckCircle,
  XCircle,
  Cloud,
  Key,
  Square,
  CheckSquare,
  Play,
  Zap,
  Users,
  Building2
} from 'lucide-react';
import { Incident } from './Dashboard';

interface SearchResultsPageProps {
  query: string;
  onBack: () => void;
  onSelectIncident: (incidentId: string) => void;
  incidents: Incident[];
  selectedIds: Set<string>;
  setSelectedIds: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const AI_PERSONAS = [
  { id: 'soc-analyst', name: 'SOC Analyst', icon: Eye, color: 'text-blue-400', bgColor: 'bg-blue-400/10', description: 'Technical deep-dive & IOC analysis' },
  { id: 'soc-manager', name: 'SOC Manager', icon: Users, color: 'text-purple-400', bgColor: 'bg-purple-400/10', description: 'Team operations & prioritization' },
  { id: 'ciso', name: 'CISO', icon: Shield, color: 'text-cyan-400', bgColor: 'bg-cyan-400/10', description: 'Risk posture & strategic response' },
  { id: 'board', name: 'Board of Directors', icon: Building2, color: 'text-orange-400', bgColor: 'bg-orange-400/10', description: 'Business impact & fiduciary duty' },
];

const generatePersonaInsights = (personaId: string, results: Incident[], query: string) => {
  const criticalCount = results.filter(r => r.severity === 'critical').length;
  const highCount = results.filter(r => r.severity === 'high').length;
  const mediumCount = results.filter(r => r.severity === 'medium').length;
  const lowCount = results.filter(r => r.severity === 'low').length;
  const activeCount = results.filter(r => r.status === 'detected').length;
  const remediatedCount = results.filter(r => r.status === 'remediated').length;
  const awsCount = results.filter(r => r.source === 'aws').length;
  const csCount = results.filter(r => r.source === 'crowdstrike').length;
  const oktaCount = results.filter(r => r.source === 'okta').length;
  
  const now = new Date();
  const reportDate = now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const reportTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  
  // Calculate time-based metrics
  const last24h = results.filter(r => (now.getTime() - r.timestamp.getTime()) < 24 * 60 * 60 * 1000).length;
  const last1h = results.filter(r => (now.getTime() - r.timestamp.getTime()) < 60 * 60 * 1000).length;
  
  // Get unique attack types
  const attackTypes = Array.from(new Set(results.map(r => r.type)));
  
  // Calculate remediation rate
  const remediationRate = results.length > 0 ? Math.round((remediatedCount / results.length) * 100) : 0;
  
  switch (personaId) {
    case 'soc-analyst':
      return {
        title: 'SOC Analyst Technical Report',
        subtitle: `Generated ${reportDate} at ${reportTime}`,
        sections: [
          { 
            heading: '📊 Incident Summary', 
            content: `Search Query: "${query}"\nTotal Incidents: ${results.length}\nTime Range: All available data\n\nSeverity Breakdown:\n• Critical: ${criticalCount} (${results.length > 0 ? Math.round((criticalCount/results.length)*100) : 0}%)\n• High: ${highCount} (${results.length > 0 ? Math.round((highCount/results.length)*100) : 0}%)\n• Medium: ${mediumCount} (${results.length > 0 ? Math.round((mediumCount/results.length)*100) : 0}%)\n• Low: ${lowCount} (${results.length > 0 ? Math.round((lowCount/results.length)*100) : 0}%)`
          },
          { 
            heading: '🎯 Attack Vector Analysis', 
            content: `Detection Sources:\n• AWS GuardDuty/CloudTrail: ${awsCount} incidents\n• CrowdStrike Falcon: ${csCount} incidents\n• Okta Identity: ${oktaCount} incidents\n\nAttack Types Observed (${attackTypes.length} unique):\n${attackTypes.slice(0, 8).map(t => `• ${t}`).join('\n')}${attackTypes.length > 8 ? `\n• ... and ${attackTypes.length - 8} more` : ''}`
          },
          { 
            heading: '🔍 IOC Extraction', 
            content: `Indicators of Compromise identified:\n\nIP Addresses:\n• 185.143.223.47 (RU - High Risk)\n• 91.234.99.42 (CN - High Risk)\n• 45.155.205.99 (Unknown VPN)\n• 194.26.29.113 (Tor Exit Node)\n\nDomains:\n• pool.minexmr.com (Cryptomining)\n• xk7hg9m2.xyz (DGA Pattern)\n\nFile Hashes:\n• Pending sandbox analysis\n\nUser Accounts:\n• ${results.filter(r => r.resource?.includes('@')).map(r => r.resource).slice(0, 3).join(', ') || 'None identified'}`
          },
          { 
            heading: '🗺️ MITRE ATT&CK Mapping', 
            content: `Tactics Observed:\n• Initial Access: T1078 (Valid Accounts), T1566 (Phishing)\n• Execution: T1059 (Command & Scripting)\n• Persistence: T1136 (Create Account)\n• Privilege Escalation: T1548 (Abuse Elevation)\n• Defense Evasion: T1070 (Indicator Removal)\n• Credential Access: T1003 (OS Credential Dumping)\n• Discovery: T1087 (Account Discovery)\n• Lateral Movement: T1021 (Remote Services)\n• Collection: T1114 (Email Collection)\n• Exfiltration: T1041 (Exfiltration Over C2)\n• Impact: T1486 (Data Encrypted), T1496 (Resource Hijacking)\n\nCoverage: 11/14 ATT&CK Tactics observed`
          },
          { 
            heading: '🔬 Forensic Recommendations', 
            content: `Immediate Actions:\n1. Preserve memory dumps from affected endpoints\n2. Collect network flow data for IOC IPs\n3. Export authentication logs for timeline analysis\n4. Snapshot affected EC2 instances before termination\n\nAnalysis Tasks:\n• Malware reverse engineering (if samples available)\n• Timeline reconstruction across all sources\n• Lateral movement path mapping\n• Data exfiltration volume estimation\n\nEvidence Retention: 90 days minimum for compliance`
          },
          { 
            heading: '✅ Remediation Status', 
            content: `Completed: ${remediatedCount}/${results.length} (${remediationRate}%)\nPending: ${activeCount} incidents\n\nMean Time to Detect (MTTD): <100ms\nMean Time to Remediate (MTTR): 1.2 seconds\n\nAutomated Actions Taken:\n• ${remediatedCount > 0 ? `${remediatedCount} threats auto-remediated` : 'No auto-remediation yet'}\n• Network isolation applied where needed\n• Credentials rotated for compromised accounts\n• IOCs added to block lists`
          }
        ]
      };
      
    case 'soc-manager':
      return {
        title: 'SOC Manager Operations Report',
        subtitle: `Generated ${reportDate} at ${reportTime}`,
        sections: [
          { 
            heading: '📈 Operational Dashboard', 
            content: `Current Queue Status:\n• Total Incidents: ${results.length}\n• Active (Requires Action): ${activeCount}\n• Remediated: ${remediatedCount}\n• Remediation Rate: ${remediationRate}%\n\nIncident Velocity:\n• Last 1 hour: ${last1h} new incidents\n• Last 24 hours: ${last24h} new incidents\n• Trend: ${last1h > 5 ? '⚠️ Elevated activity' : '✅ Normal activity'}`
          },
          { 
            heading: '👥 Team Workload Assessment', 
            content: `Recommended Staffing:\n• Current incident load: ${activeCount > 10 ? 'HIGH' : activeCount > 5 ? 'MODERATE' : 'LOW'}\n• Suggested analysts: ${Math.max(1, Math.ceil(activeCount / 5))}\n• Escalation tier: ${criticalCount > 0 ? 'Tier 3 (Critical)' : highCount > 0 ? 'Tier 2 (High)' : 'Tier 1 (Standard)'}\n\nAutomation Impact:\n• ${remediatedCount} incidents handled without analyst intervention\n• Estimated time saved: ${remediatedCount * 45} minutes\n• Analyst focus: ${activeCount} incidents requiring human review`
          },
          { 
            heading: '🎯 Priority Queue', 
            content: `P1 - CRITICAL (Immediate):\n${criticalCount > 0 ? results.filter(r => r.severity === 'critical' && r.status === 'detected').slice(0, 3).map(r => `• ${r.title}`).join('\n') || '• None' : '• None - All critical threats remediated'}\n\nP2 - HIGH (Within 1 hour):\n${highCount > 0 ? results.filter(r => r.severity === 'high' && r.status === 'detected').slice(0, 3).map(r => `• ${r.title}`).join('\n') || '• None' : '• None'}\n\nP3 - MEDIUM (Within 4 hours):\n${mediumCount > 0 ? `• ${mediumCount} incidents pending review` : '• None'}\n\nP4 - LOW (Within 24 hours):\n${lowCount > 0 ? `• ${lowCount} incidents for batch processing` : '• None'}`
          },
          { 
            heading: '📊 SLA Compliance', 
            content: `Response Time SLAs:\n• Critical: <15 min target → ✅ 1.2s actual (99% compliant)\n• High: <1 hour target → ✅ 1.2s actual (100% compliant)\n• Medium: <4 hours target → ✅ 1.2s actual (100% compliant)\n• Low: <24 hours target → ✅ 1.2s actual (100% compliant)\n\nOverall SLA Compliance: 100%\nAutomation-Enabled SLA Achievement`
          },
          { 
            heading: '🔄 Shift Handoff Notes', 
            content: `Current Shift Summary:\n• ${results.length} total incidents processed\n• ${criticalCount} critical threats ${criticalCount > 0 && activeCount > 0 ? '(some still active)' : '(all resolved)'}\n• Cross-domain attack patterns: ${results.some(r => r.isCrossDomain) ? 'DETECTED - Brief incoming shift' : 'None detected'}\n\nPending Actions for Next Shift:\n• Review ${activeCount} active incidents\n• Verify remediation completeness\n• Update runbooks if new attack patterns observed\n• Schedule threat hunting based on IOCs`
          },
          { 
            heading: '📋 Resource Recommendations', 
            content: `Immediate Needs:\n${activeCount > 10 ? '• Request additional analyst coverage\n• Consider enabling enhanced automation' : '• Current staffing adequate'}\n${criticalCount > 2 ? '• Escalate to incident commander\n• Activate IR playbook' : ''}\n\nTraining Opportunities:\n• ${awsCount > 0 ? 'AWS threat detection patterns' : ''}\n• ${csCount > 0 ? 'Endpoint detection & response' : ''}\n• ${oktaCount > 0 ? 'Identity-based attack recognition' : ''}\n\nTool Enhancements:\n• ${remediationRate < 80 ? 'Review automation rules for gaps' : 'Automation performing well'}`
          }
        ]
      };
      
    case 'ciso':
      return {
        title: 'CISO Executive Security Brief',
        subtitle: `Generated ${reportDate} at ${reportTime}`,
        sections: [
          { 
            heading: '🎯 Threat Landscape Overview', 
            content: `Current Security Posture: ${criticalCount === 0 && activeCount === 0 ? '🟢 GREEN - Stable' : criticalCount > 0 ? '🔴 RED - Elevated Risk' : '🟡 YELLOW - Monitoring'}\n\nIncident Summary:\n• Total Events: ${results.length}\n• Critical Severity: ${criticalCount}\n• Requiring Attention: ${activeCount}\n• Successfully Mitigated: ${remediatedCount} (${remediationRate}%)\n\nThreat Sources:\n• Cloud Infrastructure (AWS): ${awsCount} events\n• Endpoint Security: ${csCount} events\n• Identity & Access: ${oktaCount} events`
          },
          { 
            heading: '⚠️ Risk Assessment', 
            content: `Enterprise Risk Score: ${criticalCount > 3 ? '78/100 (HIGH)' : criticalCount > 0 ? '52/100 (MODERATE)' : '28/100 (LOW)'}\n\nRisk Factors:\n• Data Exposure Risk: ${results.some(r => r.type.includes('S3') || r.type.includes('Exfil')) ? 'ELEVATED' : 'Normal'}\n• Credential Compromise: ${results.some(r => r.type.includes('Credential') || r.type.includes('Brute')) ? 'ELEVATED' : 'Normal'}\n• Ransomware Indicators: ${results.some(r => r.type.includes('Ransomware') || r.type.includes('Encrypt')) ? 'DETECTED' : 'None'}\n• Nation-State TTPs: ${results.some(r => r.type.includes('APT') || r.title.includes('APT')) ? 'Possible' : 'Not detected'}\n\nCompliance Impact:\n• PCI-DSS: ${results.some(r => r.source === 'aws') ? 'Review required' : 'No impact'}\n• SOC 2: Evidence generated for audit\n• GDPR: ${results.some(r => r.description.includes('PII') || r.description.includes('customer')) ? 'Assessment needed' : 'No impact'}`
          },
          { 
            heading: '💰 Business Impact Analysis', 
            content: `Financial Exposure:\n• Potential breach cost avoided: $${(remediatedCount * 180000).toLocaleString()}\n  (Based on IBM Cost of Data Breach Report avg: $4.45M/breach)\n• Downtime prevented: ${remediatedCount > 0 ? '100%' : 'N/A'}\n• Regulatory fine exposure: ${criticalCount > 0 && activeCount > 0 ? 'At Risk' : 'Mitigated'}\n\nOperational Impact:\n• Service disruption: None\n• Customer notification required: ${activeCount > 0 && criticalCount > 0 ? 'Under assessment' : 'No'}\n• Insurance claim potential: ${results.length > 10 ? 'Review with carrier' : 'Not applicable'}\n\nROI on Security Investment:\n• Incidents auto-remediated: ${remediatedCount}\n• Manual effort avoided: ${remediatedCount * 4} hours\n• Cost savings: $${(remediatedCount * 4 * 150).toLocaleString()}`
          },
          { 
            heading: '🛡️ Security Program Effectiveness', 
            content: `Key Performance Indicators:\n• Mean Time to Detect: <100ms (Industry avg: 207 days)\n• Mean Time to Remediate: 1.2s (Industry avg: 70 days)\n• Automation Rate: ${remediationRate}%\n• False Positive Rate: <5%\n\nProgram Maturity:\n• Detection Capability: Advanced\n• Response Capability: Automated\n• Recovery Capability: Rapid\n• Prevention Capability: Evolving\n\nComparison to Industry:\n• 99.7% faster detection than industry average\n• 99.9% faster remediation than industry average`
          },
          { 
            heading: '📋 Strategic Recommendations', 
            content: `Immediate Actions:\n${criticalCount > 0 ? '1. Convene incident response team\n2. Assess breach notification requirements\n3. Brief legal counsel' : '1. Continue monitoring elevated activity\n2. Review automation effectiveness\n3. Update board on security posture'}\n\n30-Day Priorities:\n• Expand endpoint coverage to remaining 12% of fleet\n• Implement additional identity verification controls\n• Conduct tabletop exercise based on observed TTPs\n\n90-Day Strategic Initiatives:\n• Evaluate zero-trust architecture acceleration\n• Review cyber insurance coverage adequacy\n• Benchmark security program against NIST CSF`
          },
          { 
            heading: '📊 Board Reporting Metrics', 
            content: `Dashboard Summary for Board:\n\n┌─────────────────────────────────────┐\n│ Security Incidents: ${results.length.toString().padStart(3)}              │\n│ Auto-Remediated:    ${remediatedCount.toString().padStart(3)} (${remediationRate}%)        │\n│ Response Time:      1.2 seconds     │\n│ Risk Level:         ${criticalCount > 0 ? 'ELEVATED' : 'NORMAL'}          │\n│ Compliance Status:  ${activeCount === 0 ? 'COMPLIANT' : 'REVIEW'}         │\n└─────────────────────────────────────┘\n\nTrend: ${last24h > 10 ? '↑ Increasing activity' : '→ Stable'}`
          }
        ]
      };
      
    case 'board':
      return {
        title: 'Board of Directors Security Report',
        subtitle: `Confidential - ${reportDate}`,
        sections: [
          { 
            heading: '📌 Executive Summary', 
            content: `The organization's cybersecurity posture remains ${criticalCount === 0 && activeCount === 0 ? 'STRONG' : criticalCount > 0 ? 'under ACTIVE THREAT requiring attention' : 'STABLE with items under management'}.\n\nKey Highlights:\n• ${results.length} security events detected and analyzed\n• ${remediatedCount} threats automatically neutralized (${remediationRate}% success rate)\n• ${activeCount > 0 ? `${activeCount} items require continued monitoring` : 'All threats successfully contained'}\n• Zero confirmed data breaches\n• Zero service disruptions to customers\n\nThe AI-powered security platform continues to demonstrate significant value, reducing response times from industry-average 70+ days to 1.2 seconds.`
          },
          { 
            heading: '💵 Financial & Liability Exposure', 
            content: `Current Exposure Assessment:\n\nDirect Financial Risk:\n• Estimated breach cost avoided: $${(remediatedCount * 180000).toLocaleString()}\n• Potential regulatory fines mitigated: $${criticalCount > 0 ? '500K - $2M range' : 'Minimal'}\n• Cyber insurance deductible exposure: $${activeCount > 0 ? '250,000' : '0'}\n\nIndirect Costs Prevented:\n• Reputation damage: Avoided\n• Customer churn from breach: Avoided\n• Legal defense costs: Not triggered\n• Stock price impact: None anticipated\n\nInsurance Status:\n• Policy: Active ($10M coverage)\n• Claims YTD: 0\n• Premium impact: Favorable based on performance`
          },
          { 
            heading: '⚖️ Regulatory & Compliance Status', 
            content: `Compliance Framework Status:\n\n• SOC 2 Type II: ✅ Compliant\n  - All security events logged and auditable\n  - Automated controls functioning as designed\n\n• PCI-DSS: ✅ Compliant\n  - Cardholder data environment protected\n  - ${awsCount > 0 ? 'Cloud controls verified' : 'No relevant events'}\n\n• GDPR: ✅ Compliant\n  - No reportable data breaches (72-hour rule)\n  - Data subject rights unaffected\n\n• CCPA: ✅ Compliant\n  - California consumer data protected\n\n• SEC Cyber Disclosure: ✅ Current\n  - No material incidents requiring 8-K filing\n  - Annual 10-K disclosure on track\n\nUpcoming Audit: Q2 - No concerns identified`
          },
          { 
            heading: '🎯 Fiduciary Risk Assessment', 
            content: `Board Oversight Adequacy:\n\n✅ Risk Identification: Comprehensive monitoring in place\n✅ Risk Mitigation: Automated response capabilities active\n✅ Risk Reporting: Real-time visibility established\n✅ Resource Allocation: Security investment performing well\n\nDue Diligence Evidence:\n• Security program aligned with NIST CSF\n• Regular third-party assessments conducted\n• Incident response plan tested and validated\n• Cyber insurance coverage appropriate\n\nShareholder Value Protection:\n• Brand reputation: Protected\n• Competitive position: Maintained\n• Customer trust: Unimpacted\n• Intellectual property: Secured`
          },
          { 
            heading: '📊 Performance vs. Investment', 
            content: `Security Investment ROI:\n\nAnnual Security Spend: ~$2.5M\nValue Delivered:\n• Breach costs avoided: $${(remediatedCount * 180000).toLocaleString()}\n• Downtime prevented: $${(remediatedCount * 50000).toLocaleString()}\n• Compliance fines avoided: ~$500K\n• Reputation protection: Priceless\n\nTotal Value: $${((remediatedCount * 180000) + (remediatedCount * 50000) + 500000).toLocaleString()}\nROI: ${Math.round((((remediatedCount * 180000) + (remediatedCount * 50000) + 500000) / 2500000) * 100)}%\n\nIndustry Comparison:\n• Detection speed: 99.7% faster than peers\n• Response time: 99.9% faster than peers\n• Automation rate: Top decile performance`
          },
          { 
            heading: '📋 Board Action Items', 
            content: `Recommended Board Actions:\n\n${criticalCount > 0 && activeCount > 0 ? `1. ⚠️ URGENT: Receive briefing from CISO on active critical threats\n2. Review incident response activation criteria\n3. Assess communication strategy if escalation needed\n` : `1. ✅ Acknowledge security team performance\n2. Review quarterly security metrics\n3. Approve continued security investment\n`}\nStanding Items:\n• Annual cybersecurity strategy review: Q3\n• Cyber insurance renewal: Q4\n• Third-party security assessment: Scheduled\n• Incident response tabletop: Next quarter\n\nNo immediate board decisions required${criticalCount > 0 && activeCount > 0 ? ' pending CISO briefing' : ''}.`
          }
        ]
      };
      
    default:
      return { title: 'Analysis', subtitle: '', sections: [] };
  }
};

const SearchResultsPage: React.FC<SearchResultsPageProps> = ({ 
  query, 
  onBack, 
  onSelectIncident,
  incidents,
  selectedIds,
  setSelectedIds
}) => {
  const [sourceFilter, setSourceFilter] = useState<'all' | 'aws' | 'crowdstrike' | 'okta'>('all');
  const [severityFilter, setSeverityFilter] = useState<'all' | 'critical' | 'high' | 'medium' | 'low'>('all');
  const [selectedPersona, setSelectedPersona] = useState<string | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const searchResults = useMemo(() => {
    if (!query.trim()) return incidents;
    
    const lowerQuery = query.toLowerCase();
    return incidents.filter(incident =>
      incident.title.toLowerCase().includes(lowerQuery) ||
      incident.description.toLowerCase().includes(lowerQuery) ||
      incident.type.toLowerCase().includes(lowerQuery) ||
      incident.source.toLowerCase().includes(lowerQuery) ||
      incident.severity.toLowerCase().includes(lowerQuery) ||
      (incident.resource && incident.resource.toLowerCase().includes(lowerQuery))
    );
  }, [incidents, query]);

  const filteredResults = useMemo(() => {
    return searchResults
      .filter(i => sourceFilter === 'all' || i.source === sourceFilter)
      .filter(i => severityFilter === 'all' || i.severity === severityFilter);
  }, [searchResults, sourceFilter, severityFilter]);

  const allFilteredSelected = filteredResults.length > 0 && 
    filteredResults.every(i => selectedIds.has(i.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredResults.forEach(i => next.delete(i.id));
        return next;
      });
    } else {
      setSelectedIds(prev => {
        const next = new Set(prev);
        filteredResults.forEach(i => next.add(i.id));
        return next;
      });
    }
  };

  const toggleSelect = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getSourceIcon = (source: Incident['source']) => {
    switch (source) {
      case 'aws': return <Cloud className="w-4 h-4" />;
      case 'crowdstrike': return <Shield className="w-4 h-4" />;
      case 'okta': return <Key className="w-4 h-4" />;
    }
  };

  const getSeverityBadge = (severity: Incident['severity']) => {
    const classes = {
      critical: 'bg-red-500/20 text-red-400 border-red-500/50',
      high: 'bg-orange-500/20 text-orange-400 border-orange-500/50',
      medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50',
      low: 'bg-green-500/20 text-green-400 border-green-500/50',
    };
    return classes[severity];
  };

  const formatTime = (date: Date) => {
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return date.toLocaleDateString();
  };

  const handlePersonaClick = (personaId: string) => {
    setIsAnalyzing(true);
    setSelectedPersona(personaId);
    setTimeout(() => setIsAnalyzing(false), 1500);
  };

  const personaInsights = selectedPersona ? generatePersonaInsights(selectedPersona, filteredResults, query) : null;

  return (
    <div className="min-h-screen bg-arbitium-darker font-nunito">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-arbitium-primary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-arbitium-accent/5 rounded-full blur-3xl"></div>
      </div>

      <header className="relative z-10 bg-arbitium-dark/80 backdrop-blur-xl border-b border-arbitium-primary/10 px-6 py-4">
        <div className="flex items-center gap-4">
          <button
            onClick={onBack}
            className="p-2 text-gray-400 hover:text-white hover:bg-arbitium-primary/10 rounded-lg transition-colors"
            title="Back to Dashboard"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-3">
            <span className="text-2xl">🔍</span>
            <h1 className="text-xl font-semibold text-white">Search Results</h1>
            <span className="text-gray-400">for "{query}"</span>
            <span className="px-2 py-0.5 text-xs bg-arbitium-accent/20 text-arbitium-accent rounded-full">
              {filteredResults.length} results
            </span>
          </div>
        </div>
      </header>

      <main className="relative z-10 p-6">
        <div className="max-w-7xl mx-auto">
          {searchResults.length === 0 ? (
            <div className="bg-arbitium-dark/50 backdrop-blur-xl border border-arbitium-primary/10 rounded-xl p-12 text-center">
              <span className="text-6xl mb-4 block">🔍</span>
              <h3 className="text-xl font-medium text-white mb-2">No results found for "{query}"</h3>
              <p className="text-gray-500 mb-6">Try a different search term or generate some threats first.</p>
            </div>
          ) : (
            <div className="flex gap-6">
              <div className="flex-1">
                {/* AI Copilot Analysis */}
                <div className="bg-arbitium-dark/50 backdrop-blur-xl border border-arbitium-primary/10 rounded-xl p-4 mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Brain className="w-4 h-4 text-arbitium-accent" />
                    <span className="text-sm font-medium text-white">AI Co-pilot Incident Reporting</span>
                    <span className="text-xs text-gray-500">— Select a perspective for detailed analysis</span>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    {AI_PERSONAS.map((persona) => (
                      <button
                        key={persona.id}
                        onClick={() => handlePersonaClick(persona.id)}
                        className={`flex flex-col items-center gap-2 p-3 rounded-lg border transition-all ${
                          selectedPersona === persona.id
                            ? `${persona.bgColor} border-current ${persona.color}`
                            : 'border-arbitium-primary/10 hover:border-arbitium-primary/30 hover:bg-arbitium-primary/5'
                        }`}
                      >
                        <persona.icon className={`w-5 h-5 ${selectedPersona === persona.id ? persona.color : 'text-gray-400'}`} />
                        <span className={`text-xs font-medium text-center ${selectedPersona === persona.id ? 'text-white' : 'text-gray-400'}`}>
                          {persona.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Filters */}
                <div className="bg-arbitium-dark/50 backdrop-blur-xl border border-arbitium-primary/10 rounded-xl p-4 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-white">Filters</span>
                    <button
                      onClick={toggleSelectAll}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-400 hover:text-white transition-colors"
                    >
                      {allFilteredSelected ? (
                        <CheckSquare className="w-4 h-4 text-arbitium-accent" />
                      ) : (
                        <Square className="w-4 h-4" />
                      )}
                      {allFilteredSelected ? 'Deselect All' : 'Select All'}
                    </button>
                  </div>
                  
                  <div className="flex gap-2 mb-3">
                    {(['all', 'aws', 'crowdstrike', 'okta'] as const).map((f) => (
                      <button
                        key={f}
                        onClick={() => setSourceFilter(f)}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                          sourceFilter === f 
                            ? 'bg-arbitium-accent/20 text-arbitium-accent border border-arbitium-accent/50' 
                            : 'text-gray-400 hover:text-white hover:bg-arbitium-primary/10'
                        }`}
                      >
                        {f === 'all' ? 'All Sources' : f === 'aws' ? 'AWS' : f === 'crowdstrike' ? 'CrowdStrike' : 'Okta'}
                      </button>
                    ))}
                  </div>
                  
                  <div className="flex gap-2">
                    {(['all', 'critical', 'high', 'medium', 'low'] as const).map((s) => (
                      <button
                        key={s}
                        onClick={() => setSeverityFilter(s)}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                          severityFilter === s 
                            ? s === 'critical' ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                            : s === 'high' ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50'
                            : s === 'medium' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                            : s === 'low' ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                            : 'bg-arbitium-accent/20 text-arbitium-accent border border-arbitium-accent/50'
                            : 'text-gray-400 hover:text-white hover:bg-arbitium-primary/10'
                        }`}
                      >
                        {s === 'all' ? 'All Severities' : s.charAt(0).toUpperCase() + s.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Results List */}
                <div className="bg-arbitium-dark/50 backdrop-blur-xl border border-arbitium-primary/10 rounded-xl overflow-hidden">
                  <div className="max-h-[600px] overflow-y-auto">
                    {filteredResults.length === 0 ? (
                      <div className="p-8 text-center text-gray-400">
                        No threats match the current filters
                      </div>
                    ) : (
                      filteredResults.map((incident) => (
                        <div
                          key={incident.id}
                          className={`p-4 border-b border-arbitium-primary/10 hover:bg-arbitium-primary/5 transition-colors ${
                            incident.status === 'remediated' ? 'opacity-60' : ''
                          } ${selectedIds.has(incident.id) ? 'bg-arbitium-accent/20 border-l-4 border-l-arbitium-accent' : ''}`}
                        >
                          <div className="flex items-start gap-3">
                            <button
                              onClick={(e) => toggleSelect(incident.id, e)}
                              className="mt-1 text-gray-400 hover:text-white transition-colors"
                            >
                              {selectedIds.has(incident.id) ? (
                                <CheckSquare className="w-5 h-5 text-arbitium-accent" />
                              ) : (
                                <Square className="w-5 h-5" />
                              )}
                            </button>

                            <div 
                              className="flex-1 min-w-0 cursor-pointer" 
                              onClick={() => onSelectIncident(incident.id)}
                            >
                              <div className="flex items-center gap-2 mb-1">
                                <span className="px-2 py-0.5 text-xs rounded border flex items-center gap-1 text-white bg-transparent border-white/30">
                                  {getSourceIcon(incident.source)}
                                  {incident.source.toUpperCase()}
                                </span>
                                <span className={`px-2 py-0.5 text-xs rounded border ${getSeverityBadge(incident.severity)}`}>
                                  {incident.severity.toUpperCase()}
                                </span>
                              </div>
                              <h3 className="font-medium text-white truncate">{incident.title}</h3>
                              <p className="text-sm text-gray-400 truncate">{incident.description}</p>
                            </div>
                            
                            <div className="flex flex-col items-end gap-2">
                              {incident.status === 'remediated' ? (
                                <span className="flex items-center gap-1 text-sm text-green-400">
                                  <CheckCircle className="w-3 h-3" /> Remediated
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-sm text-red-400">
                                  <XCircle className="w-3 h-3" /> Active
                                </span>
                              )}
                              <span className="text-xs text-gray-500">{formatTime(incident.timestamp)}</span>
                              {incident.status !== 'remediated' && (
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => onSelectIncident(incident.id)}
                                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-arbitium-primary/20 text-arbitium-accent border border-arbitium-primary/30 rounded-lg hover:bg-arbitium-primary/30 transition-colors"
                                  >
                                    <Play className="w-3 h-3" />
                                    Analyze
                                  </button>
                                  <button
                                    onClick={() => onSelectIncident(incident.id)}
                                    className="flex items-center gap-1 px-3 py-1.5 text-xs bg-gradient-to-r from-green-500/20 to-emerald-500/20 text-green-400 border border-green-500/30 rounded-lg hover:from-green-500/30 hover:to-emerald-500/30 transition-colors"
                                  >
                                    <Zap className="w-3 h-3" />
                                    Auto-Remediate
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>

              {/* AI Persona Panel */}
              {selectedPersona && (
                <div className="w-[450px] shrink-0">
                  <div className="bg-arbitium-dark/50 backdrop-blur-xl border border-arbitium-primary/10 rounded-xl overflow-hidden sticky top-6">
                    <div className="flex items-center justify-between p-4 border-b border-arbitium-primary/10">
                      <div className="flex items-center gap-3">
                        {AI_PERSONAS.find(p => p.id === selectedPersona)?.icon && (
                          <div className={`p-2 rounded-lg ${AI_PERSONAS.find(p => p.id === selectedPersona)?.bgColor}`}>
                            {React.createElement(AI_PERSONAS.find(p => p.id === selectedPersona)!.icon, {
                              className: `w-5 h-5 ${AI_PERSONAS.find(p => p.id === selectedPersona)?.color}`
                            })}
                          </div>
                        )}
                        <div>
                          <h3 className="font-medium text-white">{AI_PERSONAS.find(p => p.id === selectedPersona)?.name}</h3>
                          <p className="text-xs text-gray-500">{AI_PERSONAS.find(p => p.id === selectedPersona)?.description}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => setSelectedPersona(null)}
                        className="p-1 text-gray-400 hover:text-white rounded"
                      >
                        ✕
                      </button>
                    </div>

                    <div className="p-4 max-h-[calc(100vh-200px)] overflow-y-auto">
                      {isAnalyzing ? (
                        <div className="flex flex-col items-center justify-center py-12">
                          <Loader className="w-8 h-8 text-arbitium-accent animate-spin mb-4" />
                          <p className="text-gray-400">Generating comprehensive report...</p>
                        </div>
                      ) : personaInsights ? (
                        <div className="space-y-4">
                          <div>
                            <h4 className="text-lg font-semibold text-white">{personaInsights.title}</h4>
                            {personaInsights.subtitle && (
                              <p className="text-xs text-gray-500 mt-1">{personaInsights.subtitle}</p>
                            )}
                          </div>
                          {personaInsights.sections.map((section, idx) => (
                            <div key={idx} className="bg-arbitium-darker/50 rounded-lg p-4">
                              <h5 className="text-sm font-semibold text-arbitium-accent mb-3">{section.heading}</h5>
                              <p className="text-sm text-gray-300 whitespace-pre-line font-mono leading-relaxed">{section.content}</p>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default SearchResultsPage;
